/**
 * Hook for managing user folders
 *
 * Provides folder listing, creation, and management operations.
 */

import { useState, useCallback, useEffect } from 'react';
import { api, FolderResponse } from '../api';

export interface UseFoldersOptions {
  /** Parent folder ID to list children of (null = root folders only, undefined = all folders) */
  parentFolderId?: string | null;
  /** Auto-fetch on mount */
  autoFetch?: boolean;
}

export interface UseFoldersResult {
  /** List of folders */
  folders: FolderResponse[];
  /** Whether folders are loading */
  isLoading: boolean;
  /** Error if any */
  error: Error | null;
  /** Fetch folders */
  fetchFolders: () => Promise<void>;
  /** Create a new folder */
  createFolder: (name: string, parentFolderId?: string) => Promise<FolderResponse>;
  /** Rename a folder */
  renameFolder: (folderId: string, name: string) => Promise<FolderResponse>;
  /** Move a folder */
  moveFolder: (folderId: string, newParentFolderId: string | null) => Promise<FolderResponse>;
  /** Delete a folder */
  deleteFolder: (folderId: string, deleteContents?: boolean) => Promise<void>;
  /** Get folder breadcrumb */
  getBreadcrumb: (folderId: string) => Promise<FolderResponse[]>;
  /** Get folder by ID */
  getFolder: (folderId: string) => Promise<FolderResponse>;
  /** Refresh folder list */
  refresh: () => Promise<void>;
}

/**
 * Hook for managing user folders
 */
export function useFolders(options: UseFoldersOptions = {}): UseFoldersResult {
  const { parentFolderId, autoFetch = true } = options;

  const [folders, setFolders] = useState<FolderResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchFolders = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.listFolders(parentFolderId);
      setFolders(response.folders);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch folders'));
    } finally {
      setIsLoading(false);
    }
  }, [parentFolderId]);

  const createFolder = useCallback(async (
    name: string,
    parentId?: string
  ): Promise<FolderResponse> => {
    const response = await api.createFolder({ name, parentFolderId: parentId });

    // Add to local state if in same parent
    // Handle null/undefined equivalence for root level
    const isInSameParent = (parentId ?? null) === (parentFolderId ?? null);
    if (isInSameParent) {
      setFolders((prev) => [...prev, response.folder]);
    }

    return response.folder;
  }, [parentFolderId]);

  const renameFolder = useCallback(async (
    folderId: string,
    name: string
  ): Promise<FolderResponse> => {
    const response = await api.updateFolder(folderId, { name });

    // Update local state
    setFolders((prev) =>
      prev.map((f) => (f.folderId === folderId ? response.folder : f))
    );

    return response.folder;
  }, []);

  const moveFolder = useCallback(async (
    folderId: string,
    newParentFolderId: string | null
  ): Promise<FolderResponse> => {
    const response = await api.updateFolder(folderId, { parentFolderId: newParentFolderId });

    // Remove from local state if moved to different parent
    // Handle null/undefined equivalence for root level
    const movedToDifferentParent = (newParentFolderId ?? null) !== (parentFolderId ?? null);
    if (movedToDifferentParent) {
      setFolders((prev) => prev.filter((f) => f.folderId !== folderId));
    } else {
      // Update local state
      setFolders((prev) =>
        prev.map((f) => (f.folderId === folderId ? response.folder : f))
      );
    }

    return response.folder;
  }, [parentFolderId]);

  const deleteFolder = useCallback(async (folderId: string, deleteContents: boolean = false): Promise<void> => {
    await api.deleteFolder(folderId, deleteContents);

    // Remove from local state
    setFolders((prev) => prev.filter((f) => f.folderId !== folderId));
  }, []);

  const getBreadcrumb = useCallback(async (folderId: string): Promise<FolderResponse[]> => {
    const response = await api.getFolderBreadcrumb(folderId);
    return response.breadcrumb;
  }, []);

  const getFolder = useCallback(async (folderId: string): Promise<FolderResponse> => {
    const response = await api.getFolder(folderId);
    return response.folder;
  }, []);

  const refresh = useCallback(async () => {
    await fetchFolders();
  }, [fetchFolders]);

  // Auto-fetch on mount and when parent changes
  useEffect(() => {
    if (autoFetch) {
      fetchFolders();
    }
  }, [autoFetch, fetchFolders]);

  return {
    folders,
    isLoading,
    error,
    fetchFolders,
    createFolder,
    renameFolder,
    moveFolder,
    deleteFolder,
    getBreadcrumb,
    getFolder,
    refresh,
  };
}

export default useFolders;
